import { useCallback, useState, useEffect } from 'react';
import { CareerServicesApi } from '@api/CareerService.api';
import { MasterTrackerDataResponse } from '@api/models/careerServicesApi.models';

interface UseFetchMasterTrackerDataProps {
  startDate?: Date;
  endDate?: Date;
}

interface UseFetchMasterTrackerDataReturn {
  masterTrackerData: MasterTrackerDataResponse[];
  setData: React.Dispatch<React.SetStateAction<MasterTrackerDataResponse[]>>;
  loading: boolean;
  error: Error | null;
  lastCampusSync: string;
  fetchData: (startDate: Date, endDate?: Date) => Promise<MasterTrackerDataResponse[] | null>;
}

export const useFetchMasterTrackerData = (
  initialProps?: UseFetchMasterTrackerDataProps
): UseFetchMasterTrackerDataReturn => {
  const [masterTrackerData, setMasterTrackerData] = useState<MasterTrackerDataResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastCampusSync, setLastCampusSync] = useState<string>('');

  const fetchData = useCallback(
    async (startDate: Date, endDate?: Date): Promise<MasterTrackerDataResponse[] | null> => {
      if (!startDate) {
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        const responseData = await CareerServicesApi.getMasterTrackerData(startDate, endDate);
        setMasterTrackerData(responseData.masterTrackerData);
        const lastCampusSync = responseData.lastCampusSync ? new Date(responseData.lastCampusSync).toLocaleString() : '';
        setLastCampusSync(lastCampusSync);
        setLoading(false);
        return responseData.masterTrackerData;
      } catch (err) {
        console.error('Error fetching Master Tracker data:', err);
        setError(err as Error);
        setLoading(false);
        return null;
      }
    },
    []
  );

  // Initialize with default data if provided - use useEffect instead of useState
  useEffect(() => {
    if (initialProps?.startDate) {
      fetchData(initialProps.startDate, initialProps.endDate);
    }
  }, [initialProps, fetchData]);

  return {
    masterTrackerData,
    setData: setMasterTrackerData,
    loading,
    error,
    lastCampusSync,
    fetchData,
  };
};